<template>
    <div>
        <b-card no-body class="mb-4">
            <template v-slot:header>
                <div class="d-flex justify-content-between align-content-center">
                    <h5 class="m-0">
                        {{ $t("photos_reported_issues") }} ({{ filesCount }})
                    </h5>

                    <b-form inline>
                        <label class="mr-sm-2">Filter by:</label>
                        <b-form-select
                            v-if="product.manufacturers.length > 1"
                            size="sm"
                            class="mb-2 mr-2 mb-sm-0"
                            :value="null"
                            v-model="form.manufacturer"
                            @change="getDefectPhotos()"
                        >
                            <b-select-option :value="null">
                                {{ $t("manufacturer") }}
                            </b-select-option>

                            <b-select-option
                                v-for="mf in product.manufacturers"
                                :key="mf.id"
                                :value="mf.id"
                            >
                                {{ mf.name }}
                            </b-select-option>
                        </b-form-select>

                        <b-form-select
                            size="sm"
                            class="mb-2 mb-sm-0"
                            v-model="form.defectCategory"
                            @change="getDefectPhotos()"
                        >
                            <b-select-option :value="null">
                                {{ $t("defect_category") }}
                            </b-select-option>

                            <b-select-option
                                v-for="cat in defectCategories"
                                :key="cat.id"
                                :value="cat.id"
                            >
                                {{ cat.name }}
                            </b-select-option>
                        </b-form-select>
                    </b-form>
                </div>
            </template>

            <b-card-body>
                <div class="calendar-plugin">
                    <calendar
                        :event-name="'photosDateChanged'"
                        :title="'Pictures of issues for'"
                        :controls="[
                            { title: 'Year', type: 2 },
                            { title: 'Month', type: 3 },
                            { title: 'Week', type: 4 },
                            { title: 'Day', type: 5 },
                        ]"
                        @photosDateChanged="handleDateChangedTrigger"
                    ></calendar>
                </div>

                <viewer :options="viewerOptions" v-if="!loadingFiles">

                    <loading-spinner class="loading-spinner" v-if="loadingFiles" />

                    <div v-if="!defectPhotos.length">
                        {{ $t("no_photos_attached") }}
                    </div>

                    <!-- Todo: Add pagination: you can find the pagination info in defectMeta, it is the meta property returtned by the defect photos endpoint-->
                    <div class="defect-photos-gallery">
                        <div
                            class="file d-inline-block mr-3 mb-3"
                            v-for="item in defectPhotos"
                            :key="item.id"
                            v-b-popover.hover.html="issueDescriptionFormatted(item)"
                            :title="issueTitle(item)"
                        >
                            <img
                                class="file-image mb-2"
                                width="120"
                                :src="item.square"
                                :data-source="item.url"
                                :alt="issueDescription(item)"
                            />
                            <div class="file-name">
                                {{ item.problem ? item.problem.name : "no defect" }}
                            </div>
                        </div>
                    </div>
                </viewer>

                <hr />

                <div class="d-flex justify-content-between" v-if="filesCount > 0">
                    <div class="files-count">
                        {{ $t("total_files", { count: filesCount }) }}
                    </div>

                    <div class="slider-controls-container">
                        <b-button
                            variant="link"
                            v-if="defectMeta.current_page > 1"
                            @click="getDefectPhotos(defectMeta.current_page - 1)"
                        >
                            <font-awesome-icon icon="caret-left"></font-awesome-icon>
                        </b-button>
                        <b-button
                            variant="link"
                            v-if="defectMeta.total > defectMeta.to"
                            @click="getDefectPhotos(defectMeta.current_page + 1)"
                        >
                            <font-awesome-icon icon="caret-right"></font-awesome-icon>
                        </b-button>
                    </div>
                </div>
            </b-card-body>
        </b-card>
    </div>
</template>

<script>
import axios from "axios";
import Viewer from "v-viewer/src/component.vue";
import { mapGetters, mapState } from "vuex";
import Calendar from "../../Analytics/Calendar/Calendar";
import LoadingSpinner from "../../Common/LoadingSpinner";

export default {
  name: "IssuePhotos",
  components: {
    Viewer,
    Calendar,
    LoadingSpinner
  },
  data() {
    return {
      start_date: null,
      end_date: null,
      unit: null,
      defectCategories: [],
      defectPhotos: [],
      defectMeta: {},
      loadingFiles: true,
      form: {
        manufacturer: null,
        defectCategory: null
      },
      viewerOptions: {
        toolbar: {
          zoomIn: 4,
          zoomOut: 4,
          oneToOne: 4,
          reset: 4,
          prev: 4,
          play: {
            show: 4,
            size: "large"
          },
          next: 4,
          rotateLeft: false,
          rotateRight: false,
          flipHorizontal: false,
          flipVertical: false
        },
        url: "data-source"
      }
    };
  },
  computed: {
    ...mapState("smartVaultProduct", {
      product: state => state.product,
      loading: state => state.loading
    }),
    ...mapGetters("smartVaultProduct", {
      variant: "getVariant"
    }),
    filesCount() {
      return this.defectMeta.total;
    }
  },
  watch: {
    variant(n, o) {
      this.getDefectPhotos();
    }
  },
  mounted() {
    this.getDefectPhotos();
    this.getDefectCategories();
  },
  methods: {
    handleDateChangedTrigger(event) {
      this.currentRangeName = event.rangeTitle;
      this.start_date = event.startDate;
      this.end_date = event.endDate;
      this.unit = event.unit;

      this.getDefectPhotos();
    },
    getDefectPhotos(page) {
      this.loadingFiles = true;
      let ep = this.variant
        ? `/api/variants/${this.variant.id}/defect-photos`
        : `/api/products/${this.product.id}/defect-photos`;

      axios
        .get(ep, {
          params: {
            manufacturer: this.form.manufacturer,
            problem_category: this.form.defectCategory,
            start_date: this.start_date,
            end_date: this.end_date,
            unit: this.unit,
            page: page !== undefined ? page : 1
          }
        })
        .then(r => {
          this.defectPhotos = r.data.data;
          this.defectMeta = r.data.meta;
        })
        .finally(() => {
          this.loadingFiles = false;
        });
    },
    getDefectCategories() {
      axios.get("/api/problem-categories").then(r => {
        this.defectCategories = r.data.data;
      });
    },
    issueDescription(item) {
      let issueName = item.problem ? item.problem.name : "Unknown defect";
      if (item.report !== null && typeof item.report !== "undefined") {
        return `${issueName} on Report ${item.report.report_id} (${
          item.report.report_date
        })`;
      } else {
        return issueName;
      }
    },
    issueDescriptionFormatted(item) {
      let issueName = item.problem ? item.problem.name : "Unknown defect";
      if (item.report !== null && typeof item.report !== "undefined") {
        return `${issueName} on Report ${
          item.report.report_id
        }<br> Report date: ${item.report.report_date}<br> Report comment: ${
          item.report.comment
        }`;
      } else {
        return issueName;
      }
    },
    issueTitle(item) {
      if (!item.problem) {
        return "no defect";
      }

      return `${item.problem.name} (${item.problem.category.name})`;
    }
  }
};
</script>

<style scoped>
.calendar-plugin {
  margin-bottom: 10px;
}
</style>
